// Industry × Concept benchmark matrix that powers /benchmarks/:industry/:concept.
//
// The natural pairing is drawn from each industry's own curated benchmark
// list in FOR_INDUSTRIES (each has ~4 concepts most relevant to that sector).
// We don't try to render all 11 × 38 combos — that would be doorway spam.
// Instead we render only the ~44 combinations that are actually meaningful
// for the industry (the ones the industry hub already features).
//
// Each combo becomes an SEO-optimised page targeting long-tail queries like:
//   "restaurant ebitda margin benchmark"
//   "saas rule of 40 good"
//   "construction dscr requirements"

import { FOR_INDUSTRIES, getForIndustry } from "./forIndustries";
import { getConcept } from "./learnConcepts";

// Build the full flat list of valid (industrySlug, conceptSlug) pairs.
// Only pairs where BOTH the industry and the concept exist are kept.
export function buildBenchmarkCombos() {
  const combos = [];
  for (const [industrySlug, industry] of Object.entries(FOR_INDUSTRIES)) {
    for (const b of industry.benchmark || []) {
      const conceptSlug = b.learn;
      if (!conceptSlug) continue;
      const concept = getConcept(conceptSlug);
      if (!concept) continue;
      combos.push({
        industrySlug,
        conceptSlug,
        industry,
        concept,
        // The industry-specific target/range (e.g. "60% or below" for prime cost).
        // This is what makes each page unique — it's the industry benchmark
        // for this concept, not just the concept's generic global range.
        industryTarget: {
          label: b.k,
          value: b.v,
        },
      });
    }
  }
  return combos;
}

export function getBenchmarkCombo(industrySlug, conceptSlug) {
  const industry = getForIndustry(industrySlug);
  if (!industry) return null;
  const concept = getConcept(conceptSlug);
  if (!concept) return null;
  const b = (industry.benchmark || []).find((x) => x.learn === conceptSlug);
  if (!b) return null;
  return {
    industrySlug,
    conceptSlug,
    industry,
    concept,
    industryTarget: { label: b.k, value: b.v },
  };
}

// Return the other benchmark pages inside the same industry, so each page
// can cross-link to its siblings. Great for crawl depth + user navigation.
export function getSiblingBenchmarks(industrySlug, currentConceptSlug) {
  const industry = getForIndustry(industrySlug);
  if (!industry) return [];
  return (industry.benchmark || [])
    .filter((b) => b.learn && b.learn !== currentConceptSlug)
    .map((b) => ({
      conceptSlug: b.learn,
      concept: getConcept(b.learn),
      industryTarget: { label: b.k, value: b.v },
    }))
    .filter((x) => x.concept);
}

// Return the other industry pages that also benchmark this concept.
// Powers the "compare this metric across industries" cross-nav section.
export function getConceptAcrossIndustries(conceptSlug, currentIndustrySlug) {
  const rows = [];
  for (const [industrySlug, industry] of Object.entries(FOR_INDUSTRIES)) {
    if (industrySlug === currentIndustrySlug) continue;
    const b = (industry.benchmark || []).find((x) => x.learn === conceptSlug);
    if (!b) continue;
    rows.push({
      industrySlug,
      industry,
      industryTarget: { label: b.k, value: b.v },
    });
  }
  return rows;
}

// Convert a concept slug (kebab-case) to the ratio key the backend uses
// (snake_case). e.g. "ebitda-margin" → "ebitda_margin". Used to look up
// the concept's median in the /api/peer-stats response.
export function conceptSlugToRatioKey(slug) {
  return String(slug || "").replace(/-/g, "_");
}
